// Shared-clock Web Audio multitrack engine. The vocal has fixed routing: source -> gain -> master (no panner/effects).
export class MultitrackEngine {
  constructor() {
    this.ctx = null; this.buffers = {}; this.nodes = {}; this.sources = [];
    this.playing = false; this.startCtx = 0; this.startPos = 0; this.mix = null; this.loop = null; this.raf = null; this.onTick = null;
  }
  ensure() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.master = this.ctx.createGain(); this.master.connect(this.ctx.destination);
      this.analyser = this.ctx.createAnalyser(); this.analyser.fftSize = 2048; this.master.connect(this.analyser);
    }
    return this.ctx;
  }
  async load(name, arrayBuffer) {
    const ctx = this.ensure();
    this.buffers[name] = await ctx.decodeAudioData(arrayBuffer);
    return this.buffers[name];
  }
  unload(prefix) { Object.keys(this.buffers).forEach((k) => { if (k.startsWith(prefix)) delete this.buffers[k]; }); }
  duration() { return this.buffers.vocal ? this.buffers.vocal.duration : 0; }
  position() {
    if (!this.playing) return this.startPos;
    return this.startPos + (this.ctx.currentTime - this.startCtx);
  }
  db(v) { return Math.pow(10, (v || 0) / 20); }
  applyMix(mix, trackSet) {
    this.mix = mix; this.trackSet = trackSet || this.trackSet || [];
    if (!this.ctx) return;
    const tracks = mix.tracks || {};
    const anySolo = Object.values(tracks).some((t) => t.solo) || mix.vocal?.solo;
    const t = this.ctx.currentTime;
    Object.entries(this.nodes).forEach(([name, n]) => {
      let g;
      if (name === "vocal") g = mix.vocal?.mute || (anySolo && !mix.vocal?.solo) ? 0 : this.db(mix.vocal?.gain_db);
      else { const tr = tracks[n.stem] || {}; g = tr.mute || (anySolo && !tr.solo) ? 0 : this.db(tr.gain_db); if (n.pan) n.pan.pan.setTargetAtTime(tr.pan || 0, t, 0.01); }
      n.gain.gain.setTargetAtTime(g, t, 0.01);
    });
    this.master.gain.setTargetAtTime(this.db(mix.master?.gain_db), t, 0.01);
  }
  play(from) {
    const ctx = this.ensure();
    if (ctx.state === "suspended") ctx.resume();
    this.stopSources();
    const pos = Math.max(0, Math.min(from ?? this.startPos, this.duration() - 0.01));
    const when = ctx.currentTime + 0.06;
    this.nodes = {};
    const add = (key, buf, stem, opts = {}) => {
      const src = ctx.createBufferSource(); src.buffer = buf;
      const gain = ctx.createGain(); let out = gain;
      let pan = null;
      if (stem) { pan = ctx.createStereoPanner(); gain.connect(pan); out = pan; }
      src.connect(gain); out.connect(this.master);
      const offset = (opts.offset_ms || 0) / 1000;
      const ts = opts.trim_start || 0, te = opts.trim_end || buf.duration;
      const clipStart = ts + offset; // timeline position where the trimmed clip begins
      const startIn = Math.max(0, pos - clipStart) + ts;
      if (startIn < te) {
        const delay = Math.max(0, clipStart - pos);
        src.start(when + delay, startIn, te - startIn);
      }
      this.sources.push(src); this.nodes[key] = { gain, pan, stem };
    };
    add("vocal", this.buffers.vocal, null);
    (this.trackSet || []).forEach((stem) => { const b = this.buffers[`stem:${stem}`]; if (b) add(`stem:${stem}`, b, stem, this.mix?.tracks?.[stem]); });
    this.startCtx = when; this.startPos = pos; this.playing = true;
    this.applyMix(this.mix || { tracks: {} });
    this.tick();
  }
  tick() {
    cancelAnimationFrame(this.raf);
    const step = () => {
      if (!this.playing) return;
      const p = this.position();
      if (this.loop && p >= this.loop[1]) { this.play(this.loop[0]); return; }
      if (p >= this.duration()) { this.pause(); this.startPos = 0; this.onTick && this.onTick(0); return; }
      this.onTick && this.onTick(p);
      this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }
  stopSources() { this.sources.forEach((s) => { try { s.stop(); } catch {} s.disconnect(); }); this.sources = []; }
  pause() { if (this.playing) { this.startPos = this.position(); } this.playing = false; this.stopSources(); cancelAnimationFrame(this.raf); }
  seek(t) { const was = this.playing; this.startPos = Math.max(0, Math.min(t, this.duration())); if (was) this.play(this.startPos); else this.onTick && this.onTick(this.startPos); }
  stop() { this.pause(); this.startPos = this.loop ? this.loop[0] : 0; this.onTick && this.onTick(this.startPos); }
  level() { if (!this.analyser) return 0; const a = new Float32Array(this.analyser.fftSize); this.analyser.getFloatTimeDomainData(a); let m = 0; for (const v of a) m = Math.max(m, Math.abs(v)); return m; }
  close() { this.pause(); if (this.ctx) this.ctx.close(); this.ctx = null; }
}

export function peaks(buffer, n = 1200) {
  const ch = buffer.getChannelData(0); const step = Math.max(1, Math.floor(ch.length / n)); const out = new Float32Array(n);
  for (let i = 0; i < n; i++) { let m = 0; const s = i * step; for (let j = s; j < s + step && j < ch.length; j += 4) m = Math.max(m, Math.abs(ch[j])); out[i] = m; }
  return out;
}
